import moment from 'moment';
import mapKeys from 'lodash/mapKeys';

import {apiClient} from 'common/util/api-client';
import {savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';

export enum BookmarkType {
  Addiction = `addiction`,
  Dashboard = `dashboard`,
  Formulas = `formulas`,
  FunnelQuery = `funnel-query`,
  Funnels = `funnels`,
  Insights = `insights`,
  JqlConsole = `jql-console`,
  Impact = `launch-analysis`,
  Retention = `retention`,
  Revenue = `revenue`,
  Segmentation = `segmentation`,
  Segmentation3 = `segmentation3`,
  Users = `users`,
  Cohorts = `cohort_users`,
  Flows = `flows`,
}

export interface BookmarkSaveParams {
  id?: number;
  type?: BookmarkType;
  name: string;
  params: string;
  description?: string;
  icon?: string;
  convert_segmentation_to_insights?: boolean;
  global_access_type?: string;
  // deleted=false indicates undo soft-delete
  // deleted=true (or any other value) will be rejected by backend
  deleted?: false;
  dashboard_id?: number;
}

export interface BookmarkListParams {
  id?: number[];
  type?: BookmarkType;
  includeDashboardCards?: boolean;
  eligible_for_dashboards?: boolean;
}

export interface BookmarkGetParams {
  id: number;
  convert_segmentation_to_insights?: boolean;
}

export interface BookmarkDeleteParams {
  id: number;
}

// generic bookmark type
export interface SavedBookmark<BookmarkParamsType> {
  created: string;
  deleted?: string;
  description?: string;
  displayOptions?: any;
  icon: string;
  id: number;
  includeInDashboard?: boolean;
  isDefault: boolean;
  modified: moment.Moment;
  modifiedStr?: string;
  name: string;
  originalType: string;
  params: BookmarkParamsType;
  projectId: number;
  title: string;
  type: BookmarkType;
  userId: number;
  user: string; // TODO @evnp - eliminate this confusing attr
  // TODO @evnp make these required once added to backend
  userName?: string;
  userEmail?: string;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
}

const BOOKMARK_KEY_MAP = Object.freeze({
  is_default: `isDefault`,
  original_type: `originalType`,
  project_id: `projectId`,
  user_id: `userId`,
  user_name: `userName`,
  user_email: `userEmail`,
  include_in_dashboard: `includeInDashboard`,
});

export interface Scope {
  projectId: number | null;
  workspaceId?: number | null;
}

export function getBookmarkSaveParams<BookmarkParamsType>({
  id,
  type,
  name,
  params,
  description,
  icon,
}: SavedBookmark<BookmarkParamsType>): BookmarkSaveParams {
  return {
    id,
    type,
    name,
    params: JSON.stringify(params),
    description,
    icon,
  };
}

function getBaseEndpoint({projectId, workspaceId}: Scope): string {
  const scope = workspaceId ? `api/app/workspaces/${workspaceId}` : `api/app/projects/${projectId}`;
  return `/${scope}/bookmarks`;
}

export async function createBookmark<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkSaveParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const {id, ...validParams} = params;
  const url = getBaseEndpoint(scope);
  try {
    return formatBookmark(await apiClient.post(url, validParams));
  } finally {
    if (scope.projectId) {
      savedReportsLimitStore.refresh({projectId: scope.projectId}); // async, no need to wait
    }
  }
}

export async function updateBookmark<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkSaveParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const {id, type, ...validParams} = params;
  const url = `${getBaseEndpoint(scope)}/${id}`;
  return formatBookmark(await apiClient.patch(url, validParams));
}

export function createOrUpdateBookmark<BookmarkParamsType>({
  scope,
  params,
  saveAsNew,
}: {
  scope: Scope;
  params: BookmarkSaveParams;
  saveAsNew: boolean;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  if (saveAsNew) {
    return createBookmark({scope, params});
  } else {
    return updateBookmark({scope, params});
  }
}

export async function listBookmarks<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkListParams;
}): Promise<Array<SavedBookmark<BookmarkParamsType>>> {
  const url = getBaseEndpoint(scope);
  const bookmarks: Array<SavedBookmark<BookmarkParamsType>> = await apiClient.get(url, params, {}, `bracket`);
  return bookmarks.map(formatBookmark) as Array<SavedBookmark<BookmarkParamsType>>;
}

export async function getBookmark<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkGetParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const url = `${getBaseEndpoint(scope)}/${params.id}`;
  const {id, ...nonIdParams} = params;
  return formatBookmark(await apiClient.get(url, nonIdParams));
}

export async function deleteBookmark({scope, params}: {scope: Scope; params: BookmarkDeleteParams}): Promise<number> {
  const url = `${getBaseEndpoint(scope)}/${params.id}`;
  const result = await apiClient.delete(url);
  if (scope.projectId) {
    savedReportsLimitStore.refresh({projectId: scope.projectId}); // async, no need to wait
  }
  return result[`id`];
}

function formatBookmark<BookmarkParamsType>(bookmark): SavedBookmark<BookmarkParamsType> {
  // Moment is used to correctly parse the modified date across different browsers;
  // before this additional fomatting, bookmark.modified is not understood by some browsers
  const utc = moment.utc(bookmark.modified).local();
  return {
    ...mapKeys(bookmark, (_, key) => BOOKMARK_KEY_MAP[key] || key),
    displayOptions: {chartType: bookmark.icon}, // expected by bookmark drawer
    modified: utc,
    modifiedStr: utc.format(`MMM Do, YYYY`),
    params: JSON.parse(bookmark.params),
    title: bookmark.name,
  } as SavedBookmark<BookmarkParamsType>;
}
